<template>
    <widget-card :count="data.totalAssets" :icon="QrCode" :loading="loading" :title="trans('dashboard.v2.total_number_of_assets.title')">
        <div class="position-relative">
            <div class="chart-container">
                <c-chart :data="chartData" :options="chartOptions" type="doughnut" />
            </div>

            <div class="position-absolute start-0 end-0 text-center" style="bottom: 8px">
                <div class="d-flex align-items-center justify-content-center percentage text-center">
                    <skeleton-loader v-if="loading" style="width: 48px; height: 32px; border-radius: 0.5rem" />
                    <template v-else>{{ operablePercentage + '%' }}</template>
                </div>
                <div class="label">{{ trans('general.assetstatus.operable') }}</div>
            </div>
        </div>

        <div class="legend">
            <div class="legend-col">
                <div class="legend-label gap-1"><span class="legend-dot operable"></span>{{ trans('general.assetstatus.operable') }}</div>
                <div class="legend-value">
                    <skeleton-loader v-if="loading" style="width: 70px; height: 20px; border-radius: 0.5rem" />
                    <template v-else>{{ data.operableCount + ' (' + operablePercentage + '%)' }}</template>
                </div>
            </div>
            <div class="legend-divider"></div>
            <div class="legend-col">
                <div class="legend-label gap-1"><span class="legend-dot inoperable"></span>{{ trans('general.assetstatus.inoperable') }}</div>
                <div class="legend-value">
                    <skeleton-loader v-if="loading" style="width: 70px; height: 20px; border-radius: 0.5rem" />
                    <template v-else>{{ inoperableCount + ' (' + inoperablePercentage + '%)' }}</template>
                </div>
            </div>
        </div>
    </widget-card>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import SkeletonLoader from '@/Components/SkeletonLoader.vue';
import WidgetCard from '@/Pages/DashboardV2/Components/WidgetCard.vue';
import { trans } from '@/mixins/i18n';
import QrCode from '../../../../icons/QrCode.vue';
import { eventBus, events } from '@/eventBus';
import { CChart } from '@coreui/vue-chartjs';
import { $http } from '@/bootstrap';

const props = defineProps({
    widgetData: {
        type: Object,
        required: true,
        default: () => ({}),
    },
});

const dataUrl = computed(() => props.widgetData.dataUrl);

const loading = ref(true);
const data = ref({
    totalAssets: 0,
    operableCount: 0,
});

async function fetchData(showLoading = false) {
    try {
        // Prevent loading state when refreshing the widget
        loading.value = showLoading;

        const response = await $http.get(dataUrl.value);
        data.value = {
            totalAssets: response.data.operableAssetsCount + response.data.inoperableAssetsCount,
            operableCount: response.data.operableAssetsCount,
        };
    } catch (error) {
        console.error('Failed to fetch asset status data:', error);
    } finally {
        loading.value = false;
    }
}

onMounted(() => {
    fetchData(true);
    eventBus.$on(events.dashboardWidgetRefresh, fetchData);
});

onBeforeUnmount(() => {
    eventBus.$off(events.dashboardWidgetRefresh, fetchData);
});

const inoperableCount = computed(() => data.value.totalAssets - data.value.operableCount);
const operablePercentage = computed(() => (data.value.totalAssets > 0 ? Math.round((data.value.operableCount / data.value.totalAssets) * 100) : 0));
const inoperablePercentage = computed(() => 100 - operablePercentage.value);
const showLoadingState = computed(() => loading.value || !data.value.totalAssets);

const chartData = computed(() => ({
    datasets: [
        {
            data: showLoadingState.value ? [100] : [data.value.operableCount, inoperableCount.value],
            backgroundColor: showLoadingState.value ? ['#efefef'] : ['#5577F5', '#EBB6B7'],
            borderWidth: 4,
            spacing: 0,
            borderRadius: 4,
        },
    ],
}));

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    circumference: 180,
    rotation: -90,
    cutout: '75%',
    animation: false,
    events: [],
    plugins: {
        legend: false,
    },
};
</script>

<style scoped>
.chart-container {
    width: 100%;
    max-width: 320px;
    height: 160px;
    margin: 0.5rem auto 0;
}
.percentage {
    font-size: 2.75rem;
    line-height: 2.75rem;
    font-weight: 500;
}
.label {
    color: var(--c-disabled);
    font-size: 1rem;
    line-height: 1.25rem;
    font-weight: 500;
}
.legend {
    display: flex;
    justify-content: center;
    align-items: stretch;
    gap: 0;
    margin-top: 1.875rem;
    width: 100%;
    padding: 0 2rem 1.25rem 2rem;
}
.legend-col {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}
.legend-col:first-child {
    padding-right: 1.2rem;
}
.legend-col:last-child {
    padding-left: 1.2rem;
}
.legend-label {
    display: flex;
    align-items: center;
    font-weight: 500;
    color: var(--c-body-color);
    font-size: 0.75rem;
    line-height: 0.75rem;
}
.legend-dot {
    display: inline-block;
    width: 0.625rem;
    height: 0.625rem;
    border-radius: 3px;
}
.legend-dot.operable {
    background: #5577f5;
}
.legend-dot.inoperable {
    background: #ebb6b7;
}
.legend-value {
    font-size: 1.125rem;
    line-height: 1.5rem;
    font-weight: 500;
    color: #8490a1;
    padding-left: 0.875rem;
    white-space: nowrap;
}
.legend-divider {
    width: 1px;
    background: var(--c-secondary);
}
</style>
